/**
 * PIEManagerPulsePage — batched weekly L3 capture from the manager's
 * point of view. One row per consenting direct report. Carry-forward
 * defaults pre-fill from the most recent prior submission so the
 * manager only edits what changed.
 *
 * Three rating dimensions per report (1-5):
 *   • L3 behavior frequency  — how often you saw them apply the new behavior
 *   • Consistency            — week-over-week steadiness
 *   • Impact                 — observed downstream effect
 *
 * Plus an optional free-text "friction observed" note per report.
 *
 * Submitting saves all dirty rows in a single batch upsert.
 *
 * NOT shown here: the report's own reflection text. That stays
 * between the report and the Coach. The manager is rating their
 * OWN observations only.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, ClipboardCheck, Send, CheckCircle2, ArrowLeft,
  Shield, Calendar, AlertTriangle,
} from 'lucide-react';
import PIELayout from './PIELayout';
import { PIEPageSkeleton } from './PIESkeleton';
import { pieApi } from './pieApi';
import logger from '../../lib/logger';
import './pie.css';

const SCALES = [
  {
    key: 'l3_behavior_freq',
    label: 'Behavior frequency',
    hint: 'How often did you see them apply the new behavior?',
    anchors: { 1: 'Rarely', 3: 'Sometimes', 5: 'Consistently' },
  },
  {
    key: 'consistency',
    label: 'Consistency',
    hint: 'How steady was the behavior week over week?',
    anchors: { 1: 'Sporadic', 3: 'Mixed', 5: 'Reliable' },
  },
  {
    key: 'impact',
    label: 'Impact',
    hint: 'How much downstream effect did you observe?',
    anchors: { 1: 'No change', 3: 'Some lift', 5: 'Big shift' },
  },
];

function buildInitialRow(report, carryForward, submitted) {
  const prior = submitted || carryForward || {};
  return {
    persona_id: report.id,
    l3_behavior_freq: prior.l3_behavior_freq ?? 3,
    consistency: prior.consistency ?? 3,
    impact: prior.impact ?? 3,
    friction_observed: prior.friction_observed ?? '',
    // Track whether this row is "dirty" (changed since last load)
    // so we can save a no-op POST when the manager hasn't touched it.
    _dirty: false,
    _submitted_this_week: !!submitted,
  };
}

export default function PIEManagerPulsePage() {
  const [state, setState] = useState(null);
  const [rows, setRows] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    pieApi.managerPulseCurrent()
      .then((d) => {
        setState(d);
        const next = {};
        for (const r of d.reports || []) {
          next[r.id] = buildInitialRow(
            r,
            d.carry_forward?.[r.id],
            d.submitted_this_week?.[r.id],
          );
        }
        setRows(next);
      })
      .catch((e) => {
        logger.error('managerPulseCurrent failed', e);
        setErr(e?.response?.data?.detail || 'Could not load your roster.');
      });
  }, []);

  const dirtyCount = useMemo(
    () => Object.values(rows).filter((r) => r._dirty).length,
    [rows],
  );

  const updateRow = (personaId, patch) => {
    setRows((curr) => ({
      ...curr,
      [personaId]: { ...curr[personaId], ...patch, _dirty: true },
    }));
  };

  const onSubmit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const ratings = Object.values(rows)
        .filter((r) => r._dirty)
        .map((r) => ({
          persona_id: r.persona_id,
          l3_behavior_freq: r.l3_behavior_freq,
          consistency: r.consistency,
          impact: r.impact,
          friction_observed: r.friction_observed?.trim() || null,
        }));
      if (!ratings.length) {
        setBusy(false);
        return;
      }
      await pieApi.managerPulseSubmit(ratings);
      // Re-pull so submitted-this-week rows are surfaced and dirty
      // flags are reset.
      const next = await pieApi.managerPulseCurrent();
      setState(next);
      const fresh = {};
      for (const r of next.reports || []) {
        fresh[r.id] = buildInitialRow(
          r,
          next.carry_forward?.[r.id],
          next.submitted_this_week?.[r.id],
        );
      }
      setRows(fresh);
      setSavedAt(Date.now());
    } catch (e) {
      logger.error('managerPulseSubmit failed', e);
      setErr(e?.response?.data?.detail || 'Could not save the pulse. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!state && !err) {
    return (
      <PIELayout>
        <PIEPageSkeleton cards={3} />
      </PIELayout>
    );
  }

  if (err && !state) {
    return (
      <PIELayout>
        <div className="pie-error" data-testid="pie-manager-pulse-error">{err}</div>
      </PIELayout>
    );
  }

  const reports = state?.reports || [];
  const totalCount = state?.total_count || 0;
  const submittedCount = state?.submitted_count || 0;

  return (
    <PIELayout>
      <div className="pie-canvas" data-testid="pie-manager-pulse-page">
        <section className="pie-hero-center" style={{ marginBottom: 24 }}>
          <span className="pie-eyebrow">
            <ClipboardCheck size={12} /> Manager weekly pulse · {state?.week_iso}
          </span>
          <h1 className="pie-hero-h1" style={{ marginBottom: 12 }}>
            Five minutes. <span className="pie-accent-text">Your whole team.</span>
          </h1>
          <p className="pie-hero-sub">
            One row per direct report. Last week&apos;s ratings are pre-filled — only edit what
            changed. Your observations feed the L3 (behavior) signal that calibrates the
            ROE forecast for each report.
          </p>
        </section>

        <div
          className="pie-team-privacy-banner"
          data-testid="pie-manager-pulse-banner"
          style={{ marginBottom: 18 }}
        >
          <Shield size={16} aria-hidden />
          <div>
            <div className="pie-team-privacy-headline">You&apos;re rating what you observed.</div>
            <div className="pie-team-privacy-sub">
              Reports&apos; own reflection text stays between them and the Coach.
              You only see <strong>names + roles</strong> here so you know who
              you&apos;re rating. Carry-forward defaults are loaded from the most
              recent prior week so the form is honest about what&apos;s actually changed.
            </div>
          </div>
        </div>

        {totalCount === 0 ? (
          <NoReportsState isManager={state?.is_manager} />
        ) : (
          <>
            <div
              className="pie-card"
              style={{
                marginBottom: 18, padding: '14px 18px',
                display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap',
              }}
              data-testid="pie-manager-pulse-summary"
            >
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--pie-text)' }}>
                  <Calendar size={13} aria-hidden style={{ verticalAlign: -2, marginRight: 6, color: 'var(--pie-accent)' }} />
                  {submittedCount} of {totalCount} reports already logged this week
                </div>
                <div style={{ fontSize: 12, color: 'var(--pie-text-secondary)', marginTop: 2 }}>
                  {dirtyCount > 0
                    ? `${dirtyCount} row${dirtyCount === 1 ? '' : 's'} changed — ready to submit.`
                    : 'No changes yet. Tap a number on any row to start.'}
                </div>
              </div>
              {savedAt && (
                <div
                  style={{ fontSize: 12, color: 'var(--pie-success, #10B981)', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  data-testid="pie-manager-pulse-saved-flash"
                >
                  <CheckCircle2 size={13} aria-hidden /> Saved
                </div>
              )}
              <button
                type="button"
                onClick={onSubmit}
                disabled={busy || dirtyCount === 0}
                className="pie-btn pie-btn-primary"
                data-testid="pie-manager-pulse-submit-btn"
              >
                <Send size={13} /> {busy ? 'Saving…' : `Submit ${dirtyCount || ''}`.trim()}
              </button>
            </div>

            {err && (
              <div className="pie-error" style={{ marginBottom: 12 }} data-testid="pie-manager-pulse-inline-error">
                <AlertTriangle size={14} aria-hidden style={{ verticalAlign: -2, marginRight: 6 }} />
                {err}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {reports.map((report) => (
                <ReportRow
                  key={report.id}
                  report={report}
                  row={rows[report.id]}
                  onChange={(patch) => updateRow(report.id, patch)}
                />
              ))}
            </div>

            <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onSubmit}
                disabled={busy || dirtyCount === 0}
                className="pie-btn pie-btn-primary"
                data-testid="pie-manager-pulse-submit-btn-bottom"
              >
                <Send size={13} /> {busy ? 'Saving…' : `Submit ${dirtyCount || ''}`.trim()}
              </button>
            </div>
          </>
        )}
      </div>
    </PIELayout>
  );
}

function ReportRow({ report, row, onChange }) {
  if (!row) return null;
  const submitted = row._submitted_this_week;
  return (
    <section
      className="pie-card"
      data-testid={`pie-manager-pulse-row-${report.id}`}
      style={{
        padding: '18px 20px',
        borderLeft: submitted
          ? '3px solid var(--pie-success, #10B981)'
          : (row._dirty ? '3px solid var(--pie-accent)' : '3px solid transparent'),
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--pie-text)' }}>
            {report.name}
          </div>
          <div style={{ fontSize: 12, color: 'var(--pie-text-secondary)' }}>
            {report.role_label}{report.archetype_label ? ` · ${report.archetype_label}` : ''}
          </div>
        </div>
        {submitted && (
          <span
            data-testid={`pie-manager-pulse-row-submitted-${report.id}`}
            style={{
              fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 999,
              background: 'rgba(16, 185, 129, 0.10)', color: 'var(--pie-success, #10B981)',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            <CheckCircle2 size={11} aria-hidden /> Submitted
          </span>
        )}
        {!submitted && row._dirty && (
          <span
            data-testid={`pie-manager-pulse-row-dirty-${report.id}`}
            style={{
              fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 999,
              background: 'var(--pie-accent-bg)', color: 'var(--pie-accent)',
            }}
          >
            Edited
          </span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
        {SCALES.map((s) => (
          <ScaleCell
            key={s.key}
            scale={s}
            value={row[s.key]}
            onChange={(v) => onChange({ [s.key]: v })}
            testid={`pie-manager-pulse-${report.id}-${s.key}`}
          />
        ))}
      </div>

      <div style={{ marginTop: 14 }}>
        <label
          htmlFor={`friction-${report.id}`}
          style={{ fontSize: 12, color: 'var(--pie-text-secondary)', display: 'block', marginBottom: 6 }}
        >
          Friction you observed (optional)
        </label>
        <textarea
          id={`friction-${report.id}`}
          value={row.friction_observed}
          onChange={(e) => onChange({ friction_observed: e.target.value })}
          placeholder="e.g. Booked into back-to-back meetings — no time to apply the new framework."
          rows={2}
          data-testid={`pie-manager-pulse-friction-${report.id}`}
          style={{
            width: '100%', padding: '10px 12px', borderRadius: 8,
            border: '1px solid var(--pie-border)', resize: 'vertical',
            fontSize: 13, color: 'var(--pie-text)', background: 'var(--pie-surface)',
            fontFamily: 'inherit',
          }}
        />
      </div>
    </section>
  );
}

function ScaleCell({ scale, value, onChange, testid }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--pie-text)', marginBottom: 4, fontWeight: 600 }}>
        {scale.label}
      </div>
      <div style={{ fontSize: 11, color: 'var(--pie-text-muted)', marginBottom: 8 }}>
        {scale.hint}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }} data-testid={testid}>
        {[1, 2, 3, 4, 5].map((n) => {
          const active = value === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              data-testid={`${testid}-${n}`}
              style={{
                width: 32, height: 32, borderRadius: 8,
                border: `1px solid ${active ? 'var(--pie-accent)' : 'var(--pie-border)'}`,
                background: active ? 'var(--pie-accent)' : 'transparent',
                color: active ? '#fff' : 'var(--pie-text-secondary)',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.12s ease',
              }}
            >
              {n}
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 10, color: 'var(--pie-text-muted)', marginTop: 6, minHeight: 14 }}>
        {scale.anchors[value] || ''}
      </div>
    </div>
  );
}

function NoReportsState({ isManager }) {
  return (
    <section
      className="pie-card"
      style={{ textAlign: 'center', padding: '40px 24px' }}
      data-testid="pie-manager-pulse-empty"
    >
      <Users size={28} style={{ color: 'var(--pie-accent)', marginBottom: 12 }} aria-hidden />
      <h2 className="pie-display-md" style={{ marginBottom: 8 }}>
        {isManager
          ? 'No consenting reports yet.'
          : 'You don\u2019t have any direct reports.'}
      </h2>
      <p style={{ fontSize: 14, color: 'var(--pie-text-secondary)', maxWidth: 480, margin: '0 auto 16px', lineHeight: 1.6 }}>
        {isManager
          ? 'Reports must opt-in to the manager view before you can rate their behavior. Once they do, this page will fill in.'
          : 'When direct reports are assigned to your persona, the weekly manager pulse will surface here.'}
      </p>
      <Link to="/mirror/team" className="pie-btn pie-btn-ghost" data-testid="pie-manager-pulse-back-team">
        <ArrowLeft size={13} /> Back to My team
      </Link>
    </section>
  );
}
